/**
 * The logged-in nav destinations the {@link AppShell} sidebar can mark as the
 * current page. Kept here (the pure-helper leaf) rather than in the client
 * `app-shell` component so the derivation function and the component share one
 * source of truth without the component being a dependency of this module.
 *
 * All authenticated routes live under the `/app` prefix; `/batch-transcription`
 * stays public at the root (its anonymous-trial flow renders for signed-out
 * visitors), so its match below is NOT `/app`-prefixed.
 */
export type AppShellActive =
  | 'transcribe'
  | 'transcriptions'
  | 'account'
  | 'team'
  | 'api-keys'
  | 'templates'
  | 'dictionaries'
  | 'referral'

/**
 * Derive which sidebar nav item is current from the App Router pathname, so the
 * shell can be mounted once in a route-group layout instead of every page
 * threading an explicit `active` prop.
 *
 * Order matters: `/app/account/team` is a prefix-superset of `/app/account`,
 * so it is matched first; `/batch-transcription` is anchored so it never
 * collides with `/app/transcriptions`. An unknown logged-in pathname degrades to the home
 * (`transcriptions` — the member landing now that uploading is a modal there)
 * rather than throwing — an unexpected route should never crash the shell.
 */
import { APP_ROUTES } from './app-routes'

export function pathnameToAppShellActive(pathname: string | null | undefined): AppShellActive {
  // `usePathname()` is typed `string | null` in Next's compat overload; degrade
  // to the home highlight rather than throwing on a null/empty path.
  if (!pathname) return 'transcriptions'
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname

  // Public trial-entry forwarder — stays at the root, NOT under /app.
  if (path === '/batch-transcription' || path.startsWith('/batch-transcription/')) {
    return 'transcribe'
  }
  if (path === APP_ROUTES.transcriptions || path.startsWith(`${APP_ROUTES.transcriptions}/`)) {
    return 'transcriptions'
  }
  if (path === APP_ROUTES.referral || path.startsWith(`${APP_ROUTES.referral}/`)) {
    return 'referral'
  }
  // Team before the generic account branch — it is a prefix-superset of /app/account.
  if (path === APP_ROUTES.team || path.startsWith(`${APP_ROUTES.team}/`)) {
    return 'team'
  }
  if (path === APP_ROUTES.apiKeys || path.startsWith(`${APP_ROUTES.apiKeys}/`)) {
    return 'api-keys'
  }
  if (path === APP_ROUTES.templates || path.startsWith(`${APP_ROUTES.templates}/`)) {
    return 'templates'
  }
  if (path === APP_ROUTES.dictionaries || path.startsWith(`${APP_ROUTES.dictionaries}/`)) {
    return 'dictionaries'
  }
  if (path === APP_ROUTES.account || path.startsWith(`${APP_ROUTES.account}/`)) {
    return 'account'
  }
  return 'transcriptions'
}
